import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { submitPaymentRequest, getFundCategories, getMySubscriptions, getChurchProfile, getLencoConfig, initiateLenco, verifyLenco, getLocalLencoStatus, getLiveFxRate } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { FiUpload, FiX, FiImage, FiSmartphone } from 'react-icons/fi';
import Toast from '../../components/Toast';
import { launchLencoWidget } from '../../services/lencoWidget';

const SubmitPayment = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lockedTypeId = searchParams.get('type');
  const lockedUSD = parseFloat(searchParams.get('usd') || 0);
  const lockedKwacha = parseFloat(searchParams.get('kwacha') || 0);
  const hasPledge = lockedUSD > 0 || lockedKwacha > 0;
  const fileRef = useRef();
  const [funds, setFunds] = useState([]);
  const [form, setForm] = useState({
    fund_category_id: lockedTypeId || '',
    amounts: {},
    payment_method: 'mobile', notes: ''
  });
  const [currencies, setCurrencies] = useState(['$', 'K']);
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [autoFilled, setAutoFilled] = useState(false);
  // v1.9.46 — cross-currency: pay a $ pledge in K.
  // v1.9.47 — rate is fetched live (open.er-api.com, cached 6h server-side)
  // and NOT editable — members shouldn't be able to fudge the exchange rate.
  // Falls back to admin's default_fx_rate setting if the live API fails.
  const [payDollarInK, setPayDollarInK] = useState(false);
  const [fxRate, setFxRate] = useState(24);
  const [fxRateSource, setFxRateSource] = useState('');
  // v1.7.0 — Lenco Pay state
  const [lencoConfigured, setLencoConfigured] = useState(false);
  const [lencoPaying, setLencoPaying] = useState(false);
  // v1.9.108 — collection-fee gross-up: % the backend adds to the widget charge
  // so the church receives the full pledge. 0 = feature off (default).
  const [lencoFeePercent, setLencoFeePercent] = useState(0);
  // v1.8.0 — Lenco-Pay-first redesign. When the widget is available, the
  // screenshot-upload flow collapses behind an "Already paid? Upload proof"
  // toggle so members see Mobile Money as the primary path. If Lenco is not
  // configured we fall back to the old always-visible upload form.
  const [showManualForm, setShowManualForm] = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  useEffect(() => {
    getFundCategories().then(r => setFunds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getMySubscriptions().then(r => setSubscriptions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getLencoConfig().then(r => {
      setLencoConfigured(!!r.data?.configured);
      setLencoFeePercent(parseFloat(r.data?.fee_percent || 0) || 0);
    }).catch(() => { setLencoConfigured(false); setLencoFeePercent(0); });
    getChurchProfile().then(r => {
      const list = Array.isArray(r.data?.currencies) && r.data.currencies.length ? r.data.currencies : ['$', 'K'];
      setCurrencies(list);
      // v1.9.46 — pick up admin-set default FX rate (K per $) as initial seed
      // while the live rate is loading.
      const rate = parseFloat(r.data?.default_fx_rate || 0) || 24;
      setFxRate(rate);
      setFxRateSource('default');
      // Seed locked pledge from URL params (?usd=&kwacha=) using the first two currencies.
      setForm(p => {
        const next = { ...p.amounts };
        if (lockedUSD > 0 && list[0]) next[list[0]] = lockedUSD;
        if (lockedKwacha > 0 && list[1]) next[list[1]] = lockedKwacha;
        return { ...p, amounts: next };
      });
    }).catch(() => {});
    // v1.9.47 — fetch today's live ZMW/USD rate (falls back on server-side
    // to admin's default if the API is down). Overrides the profile default.
    getLiveFxRate().then(r => {
      const n = parseFloat(r.data?.rate || 0);
      if (n > 0) {
        setFxRate(n);
        setFxRateSource(r.data?.source || 'live');
      }
    }).catch(() => { /* keep the profile default we already set */ });
  }, []); // eslint-disable-line

  // When the user picks a payment type from the dropdown, if they're already
  // subscribed to that type, auto-fill the pledge amounts (same as clicking
  // "Make Payment" on the My Contributions page).
  const handleFundChange = (e) => {
    const id = e.target.value;
    setForm(p => ({ ...p, fund_category_id: id }));
    if (!id) { setAutoFilled(false); return; }
    const sub = subscriptions.find(s => String(s.fund_category_id) === String(id));
    const subPledges = sub?.pledges || (sub ? Object.fromEntries([
      [currencies[0], parseFloat(sub.intended_amount_usd) > 0 ? sub.intended_amount_usd : 0],
      [currencies[1], parseFloat(sub.intended_amount_kwacha) > 0 ? sub.intended_amount_kwacha : 0],
    ].filter(([, v]) => parseFloat(v) > 0)) : null);
    if (subPledges && Object.keys(subPledges).length) {
      setForm(p => ({ ...p, fund_category_id: id, amounts: { ...subPledges } }));
      setAutoFilled(true);
    } else {
      setAutoFilled(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScreenshot(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.fund_category_id) return setError(t('submitPayment.selectPaymentType'));
    const cleanAmounts = Object.fromEntries(Object.entries(form.amounts || {}).filter(([, v]) => parseFloat(v) > 0).map(([k, v]) => [k, parseFloat(v)]));
    if (Object.keys(cleanAmounts).length === 0) return setError(t('submitPayment.enterAmount'));
    if (!screenshot) return setError(t('submitPayment.attachScreenshot'));

    // v1.9.46 — if "Also pay $ pledge in K" is on, add the converted K slice
    // to Amount K and record the flag + rate so the backend can split credits
    // once the cashier approves the payment_request into a CR.
    // v1.9.48 — no rounding: exact K slice earmarked for the $ pledge.
    const usdOwed = parseFloat(cleanAmounts[currencies[0]] || 0);
    const kExtra  = payDollarInK ? (usdOwed * (parseFloat(fxRate) || 0)) : 0;
    if (kExtra > 0) {
      cleanAmounts[currencies[1]] = (parseFloat(cleanAmounts[currencies[1]] || 0) + kExtra);
      // Remove the $ side since it's fully converted to K for this manual pay.
      delete cleanAmounts[currencies[0]];
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'amounts') return; // serialised below
        fd.append(k, v);
      });
      fd.append('amounts', JSON.stringify(cleanAmounts));
      if (payDollarInK && kExtra > 0) {
        fd.append('apply_to_dollar_pledge', '1');
        fd.append('fx_rate', String(fxRate));
        fd.append('kwacha_to_dollar', String(kExtra));
      }
      if (screenshot) fd.append('screenshot', screenshot);
      await submitPaymentRequest(fd);
      showToast(t('submitPayment.submittedSuccessfully'));
      setTimeout(() => navigate('/member/my-payments'), 1500);
    } catch (e) { setError(e.response?.data?.error || t('submitPayment.failedSubmit')); }
    finally { setSaving(false); }
  };

  // v1.7.0 — pay directly with mobile money via Lenco widget. Skips the
  // screenshot upload entirely: the widget confirms the payment, our backend
  // verifies it against Lenco, creates the CR and settles subscription
  // periods automatically. Only the Kwacha amount is charged.
  const handleLencoPay = async () => {
    setError('');
    if (!form.fund_category_id) return setError(t('submitPayment.selectPaymentType'));
    const k = parseFloat(form.amounts?.[currencies[1]] || form.amounts?.K || 0) || 0;
    if (k <= 0) return setError(t('submitPayment.enterAmountK', { defaultValue: 'Enter an amount in K to pay with mobile money' }));
    // v1.9.46 — if member ticked "Also pay $ pledge in K", add the converted K
    // slice to the mobile-money charge and tell the backend to route it to
    // the $ ledger via fx_rate. Backend does the actual credit split.
    // v1.9.48 — no rounding: send the exact multiplication result as
    // `kwacha_to_dollar` so the K→$ math is lossless. Mobile-money accepts
    // K414.77 fine, and the $ pledge clears exactly.
    const usdOwed = parseFloat(form.amounts?.[currencies[0]] || 0);
    const kExtra  = payDollarInK ? (usdOwed * (parseFloat(fxRate) || 0)) : 0;
    const totalK  = k + kExtra;
    setLencoPaying(true);
    try {
      const r = await initiateLenco({
        amount_kwacha: totalK,
        fund_category_id: form.fund_category_id,
        notes: form.notes,
        apply_to_dollar_pledge: payDollarInK && kExtra > 0 ? 1 : 0,
        fx_rate: payDollarInK && kExtra > 0 ? parseFloat(fxRate) : null,
        kwacha_to_dollar: payDollarInK && kExtra > 0 ? kExtra : 0,
      });
      const init = r.data;
      let alreadyDone = false;
      const finalize = async () => {
        if (alreadyDone) return;
        alreadyDone = true;
        try {
          const v = await verifyLenco(init.reference);
          // v1.8.2 — broadcast on both success paths so the persistent debt
          // banner, MemberDashboard's contribution status, and any subscribed
          // page re-fetch immediately. Without this, "You owe K…" stayed on
          // screen until the member closed and re-opened the app.
          window.dispatchEvent(new Event('subscription-updated'));
          if (v.data?.approved) {
            showToast(t('submitPayment.lencoPaid', { defaultValue: 'Payment received! CR ' }) + (v.data.cr_number || ''));
            setTimeout(() => navigate('/member/my-payments'), 1500);
          } else if (v.data?.rejected) {
            setError(t('submitPayment.lencoFailed', { defaultValue: 'Payment failed. Please try again.' }));
          } else {
            showToast(t('submitPayment.lencoPending', { defaultValue: 'Payment received — awaiting confirmation.' }));
            setTimeout(() => navigate('/member/my-payments'), 1500);
          }
        } catch (e) {
          setError(e.response?.data?.error || t('submitPayment.lencoVerifyFailed', { defaultValue: 'Could not verify payment. Check My Payments in a minute.' }));
        }
      };
      // v1.9.4 — background poll of our OWN DB status (no Lenco round-trip)
      // catches the case where the user closes the widget early but Lenco
      // still confirms via reconciliation/webhook a few seconds later.
      // Runs every 5 s for up to 4 minutes. First matching finalize wins,
      // so this races safely against the widget's onSuccess handler.
      const pollStart = Date.now();
      const POLL_MS = 5000;
      const POLL_MAX_MS = 4 * 60 * 1000;
      const pollId = setInterval(async () => {
        if (alreadyDone || Date.now() - pollStart > POLL_MAX_MS) {
          clearInterval(pollId);
          return;
        }
        try {
          const s = await getLocalLencoStatus(init.reference);
          const st = s.data?.status;
          if (st === 'approved') {
            clearInterval(pollId);
            if (alreadyDone) return;
            alreadyDone = true;
            window.dispatchEvent(new Event('subscription-updated'));
            showToast(t('submitPayment.lencoPaid', { defaultValue: 'Payment received! CR ' }) + (s.data.cr_number || ''));
            setTimeout(() => navigate('/member/my-payments'), 1500);
          } else if (st === 'rejected') {
            clearInterval(pollId);
            if (alreadyDone) return;
            alreadyDone = true;
            setLencoPaying(false);
            setError(s.data?.rejection_reason || t('submitPayment.lencoFailed', { defaultValue: 'Payment failed. Please try again.' }));
          }
          // status still 'pending' → keep polling.
        } catch (_) { /* transient network — try again next tick */ }
      }, POLL_MS);

      await launchLencoWidget({
        publicKey: init.public_key,
        scriptUrl: init.widget_script_url,
        reference: init.reference,
        amount: init.amount,
        email: init.email,
        firstName: init.firstName,
        lastName: init.lastName,
        phone: init.phone,
        label: init.label,
        onSuccess: () => { finalize(); },
        onConfirmationPending: () => { finalize(); },
        onClose: () => {
          // Widget dismissed but we KEEP polling — the background reconciler
          // may still finalize after the user closes the popup. Poll timeout
          // handles giving up gracefully.
          setLencoPaying(false);
        },
      });
    } catch (e) {
      // v1.9.30 — surface the actual throw so we stop guessing what's failing.
      // Backend errors come from e.response?.data?.error; widget throws come
      // from e?.message. Falls back to generic label only if neither exists.
      // eslint-disable-next-line no-console
      console.error('[lenco-pay] failed to start payment:', e);
      const detail = e?.response?.data?.error || e?.message || '';
      setError(detail
        ? `Payment error: ${detail}`
        : t('submitPayment.lencoInitFailed', { defaultValue: 'Could not start the payment. Please try again.' }));
      setLencoPaying(false);
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.submitPayment')}</h1></div>
      </div>

      <div className="card" style={{ maxWidth: 640 }}>
        <div className="card-body">
          {error && <div className="error-msg">{error}</div>}

          {/* Info banner */}
          <div style={{ background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#3730a3' }}>
            <strong>{t('submitPayment.howItWorks')}</strong>{' '}
            {lencoConfigured
              ? t('submitPayment.howItWorksDescLenco', { defaultValue: 'Pick a fund and amount, then pay directly with Airtel or MTN Mobile Money. Your receipt is created automatically — no screenshot needed.' })
              : t('submitPayment.howItWorksDesc')}
          </div>

          {/* Fund + amount (always visible — these drive both Lenco and manual flows) */}
          <div className="form-grid">
            <div className="form-group form-full">
              <label>{t('finance.fund')} <span className="required">*</span></label>
              {lockedTypeId ? (
                <div style={{ padding: '9px 12px', background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8, fontWeight: 600, color: '#4f46e5' }}>
                  {funds.find(f => String(f.id) === String(lockedTypeId))?.name || t('common.loading')}
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400, marginLeft: 8 }}>{t('submitPayment.locked')}</span>
                </div>
              ) : (() => {
                // v1.9.101 — Fund dropdown is split in two sections:
                //   TOP: subscription-type funds (fc.frequency is set) that
                //        this specific member is subscribed to. Hides funds
                //        with recurring frequency the member has no
                //        subscription against — so a Tithe-only member can't
                //        accidentally pay into Monthly Contribution.
                //   BOTTOM: no-frequency funds (one-off collections like
                //        Baptism, Wedding, Offering, Father's salary) are
                //        always shown so a member can still make an ad-hoc
                //        donation even if they're not "subscribed" to those.
                const subFundIds = new Set(subscriptions.map(s => String(s.fund_category_id)));
                const subscriptionFunds = funds.filter(f => f.frequency && subFundIds.has(String(f.id)));
                const oneOffFunds       = funds.filter(f => !f.frequency);
                return (
                  <select value={form.fund_category_id} onChange={handleFundChange}>
                    <option value="">—</option>
                    {subscriptionFunds.length > 0 && (
                      <optgroup label="My subscriptions">
                        {subscriptionFunds.map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {oneOffFunds.length > 0 && (
                      <optgroup label="Others (one-off)">
                        {oneOffFunds.map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                );
              })()}
              {autoFilled && !lockedTypeId && (
                <small style={{ color: '#16a34a', fontSize: 11, marginTop: 4, display: 'block' }}>
                  ✓ Amount auto-filled from your pledge — you can still change it.
                </small>
              )}
            </div>

            {/* v1.9.102 — two UX guards on the amount inputs:
                  1. mutual exclusivity: once one currency has a value, the
                     other input is disabled (prevents a double-typed
                     submission like the $1,100-and-K0 phantom on
                     2026-10-10).
                  2. $ typed -> auto-tick the existing "Also pay my $
                     pledge in K" box so mobile money gets a valid K
                     equivalent to actually charge; untyping clears it. */}
            {currencies.map(sym => {
              const otherSym = currencies.find(s => s !== sym);
              const otherHasValue = parseFloat(form.amounts[otherSym] || 0) > 0;
              const isDisabled = otherHasValue;
              return (
                <div className="form-group" key={sym}>
                  <label>Amount {sym}</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.amounts[sym] || ''}
                    disabled={isDisabled}
                    onChange={e => {
                      const next = e.target.value;
                      setForm(p => ({ ...p, amounts: { ...p.amounts, [sym]: next } }));
                      if (sym === currencies[0] && currencies[1]) {
                        setPayDollarInK(parseFloat(next || 0) > 0);
                      }
                    }}
                    placeholder={isDisabled ? '— clear the other field first' : '0.00'}
                    style={isDisabled ? { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' } : undefined}
                  />
                </div>
              );
            })}
          </div>

          {/* v1.9.46 — Cross-currency: pay a $ pledge in K.
              Shown only when the member has a $ pledge amount on the picked
              fund (either auto-filled or entered manually). Ticking the box
              adds an extra K slice to the payment (= $amount × rate) which
              the backend converts back to $ credit and applies to the $ ledger.
              Rate defaults to admin's setting, member can override per payment. */}
          {parseFloat(form.amounts?.[currencies[0]] || 0) > 0 && (
            <div style={{ marginTop: 12, padding: '12px 14px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10 }}>
              {/* v1.9.108 — checkbox locked ON while a $ amount is entered.
                  Mobile money is K-only, so a $ pledge MUST be converted; letting
                  the member untick it would strand the $ portion with no way to
                  charge it. Clearing the $ input unlocks the checkbox again. */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'not-allowed' }}>
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  readOnly
                  style={{ marginTop: 3, cursor: 'not-allowed' }}
                />
                <div style={{ flex: 1, fontSize: 13, color: '#3730a3' }}>
                  <strong>Also pay my {currencies[0]} pledge in {currencies[1]}</strong>
                  <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#c7d2fe', color: '#3730a3', textTransform: 'uppercase', letterSpacing: 0.5 }}>Required</span>
                  <div style={{ fontSize: 11, color: '#4338ca', marginTop: 2 }}>
                    Mobile money is {currencies[1]}-only, so your {currencies[0]} pledge
                    is auto-converted at today's rate and charged together with the
                    base {currencies[1]} amount. Clear the {currencies[0]} field to unlock.
                  </div>
                </div>
              </label>
              {payDollarInK && (() => {
                const usdOwed = parseFloat(form.amounts?.[currencies[0]] || 0);
                // v1.9.48 — no rounding. Send the exact multiplication result
                // so the K→$ math is lossless and the $ pledge clears cleanly.
                const kExtra  = usdOwed * (parseFloat(fxRate) || 0);
                const baseK   = parseFloat(form.amounts?.[currencies[1]] || 0);
                const totalK  = baseK + kExtra;
                const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return (
                  <div style={{ marginTop: 10, paddingLeft: 26 }}>
                    {/* v1.9.47 — rate is READ-ONLY, fetched live from the
                        market. Members shouldn't be able to fudge exchange
                        rates. Small "live" tag makes the source explicit. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: '#3730a3' }}>
                      <span>Rate:</span>
                      <strong style={{ fontSize: 14, color: '#111827' }}>
                        {currencies[1]}{fmt(fxRate)} / {currencies[0]}
                      </strong>
                      {fxRateSource && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: fxRateSource.includes('open.er-api') ? '#dcfce7' : '#e0e7ff', color: fxRateSource.includes('open.er-api') ? '#166534' : '#4338ca', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {fxRateSource.includes('open.er-api') ? 'live' : 'fallback'}
                        </span>
                      )}
                    </div>
                    <div style={{ background: '#fff', border: '1px dashed #c7d2fe', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#374151' }}>
                      <div>Base {currencies[1]}: <strong>{currencies[1]}{fmt(baseK)}</strong></div>
                      <div>+ {currencies[0]}{fmt(usdOwed)} × {fmt(fxRate)} = <strong>{currencies[1]}{fmt(kExtra)}</strong> ({currencies[0]} pledge)</div>
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #e0e7ff', fontWeight: 700 }}>
                        Total to pay: <span style={{ color: '#16a34a' }}>{currencies[1]}{fmt(totalK)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* v1.8.0 — PRIMARY path: pay directly with mobile money. Big card
              always shown when Lenco is configured; wraps the screenshot flow
              behind a collapsed toggle below. */}
          {lencoConfigured && (() => {
            // v1.9.108 — compute the gross-up preview the backend will apply.
            // The backend reads LENCO_FEE_PERCENT and inflates the widget charge
            // so the church nets the full pledge. We mirror the math here so the
            // member sees exactly what will be deducted before they tap Pay.
            // Whether they pay K alone or $ pledge converted into K, the fee is
            // on the TOTAL K that reaches the widget — same math, one surface.
            const baseK  = parseFloat(form.amounts?.[currencies[1]] || form.amounts?.K || 0) || 0;
            const usdPld = parseFloat(form.amounts?.[currencies[0]] || 0) || 0;
            const kExtra = payDollarInK ? (usdPld * (parseFloat(fxRate) || 0)) : 0;
            const totalK = baseK + kExtra;
            // v1.9.109 — mirror backend grossUp: charge = pledge / (1 − fee%/100)
            // so Lenco's fee (taken from the CHARGED amount) still leaves the
            // church with ≥ the pledge. Matches backend/services/lenco.js.
            const feeK   = (lencoFeePercent > 0 && lencoFeePercent < 100 && totalK > 0)
              ? Math.ceil(totalK / (1 - lencoFeePercent / 100) * 100) / 100 - totalK
              : 0;
            const chargeK = totalK + feeK;
            const feeK2  = Math.round(feeK * 100) / 100;
            const fmt    = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const showFee = lencoFeePercent > 0 && totalK > 0;
            return (
            <>
              <div style={{ marginTop: 16, padding: '18px 18px', background: 'linear-gradient(135deg,#ecfdf5 0%,#f0fdf4 100%)', border: '2px solid #86efac', borderRadius: 12, boxShadow: '0 2px 8px rgba(22,101,52,.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <FiSmartphone style={{ fontSize: 22, color: '#16a34a' }} />
                  <div style={{ fontWeight: 700, color: '#166534', fontSize: 16 }}>
                    {t('submitPayment.payWithMobileMoneyTitle', { defaultValue: 'Pay directly with mobile money' })}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: '#166534', marginBottom: 14 }}>
                  {t('submitPayment.payWithMobileMoneyHintV2', { defaultValue: 'Airtel Money or MTN Mobile Money. You will get a PIN prompt on your phone. Receipt is automatic — no screenshot upload needed.' })}
                </div>

                {/* v1.9.108 — service-fee breakdown. Shown only when the server
                    advertises LENCO_FEE_PERCENT > 0 and we have a K amount to
                    charge. Matches the cross-currency panel's typography so the
                    page reads as one unit. */}
                {showFee && (
                  <div style={{ background: '#fff', border: '1px dashed #86efac', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#166534' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>{t('submitPayment.fundAmount', { defaultValue: 'Fund amount' })}:</span>
                      <strong>{currencies[1]}{fmt(totalK)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span>{t('submitPayment.serviceFee', { defaultValue: 'Service fee' })} ({lencoFeePercent}%):</span>
                      <strong>+ {currencies[1]}{fmt(feeK2)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, borderTop: '1px solid #d1fae5', fontWeight: 700, fontSize: 13 }}>
                      <span>{t('submitPayment.youWillBeCharged', { defaultValue: 'You will be charged' })}:</span>
                      <span style={{ color: '#16a34a' }}>{currencies[1]}{fmt(chargeK)}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#4b5563', marginTop: 6, lineHeight: 1.4 }}>
                      {t('submitPayment.feeExplainer', { defaultValue: 'The service fee covers the mobile-money collection cost so the church receives the full pledge amount.' })}
                    </div>
                  </div>
                )}

                <button
                  className="btn btn-success"
                  onClick={handleLencoPay}
                  disabled={lencoPaying || saving}
                  style={{ background: '#16a34a', color: '#fff', width: '100%', padding: '12px 18px', fontSize: 15, fontWeight: 700 }}
                >
                  <FiSmartphone /> {lencoPaying
                    ? t('submitPayment.lencoPayingLong', { defaultValue: 'Waiting for confirmation (up to 2 min)…' })
                    : (showFee
                      ? t('submitPayment.payNowMobileMoneyAmount', { defaultValue: `Pay ${currencies[1]}${fmt(chargeK)} with Mobile Money`, amount: `${currencies[1]}${fmt(chargeK)}` })
                      : t('submitPayment.payNowMobileMoney', { defaultValue: 'Pay with Mobile Money' }))}
                </button>
              </div>
              {lencoPaying && (
                <div style={{ marginTop: 10, padding: '10px 14px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#92400e' }}>
                  <strong>{t('submitPayment.lencoWaitTitle', { defaultValue: 'Confirming payment…' })}</strong>{' '}
                  {t('submitPayment.lencoWaitBody', { defaultValue: 'This can take up to 2 minutes. Please do NOT close this page or cancel — the mobile-money confirmation is on its way.' })}
                </div>
              )}

              {/* "or — already paid? upload proof" toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 10px' }}>
                <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>{t('common.or', { defaultValue: 'or' })}</span>
                <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
              </div>
              <button
                type="button"
                onClick={() => setShowManualForm(v => !v)}
                disabled={lencoPaying}
                style={{ width: '100%', padding: '10px 14px', background: showManualForm ? '#eef2ff' : '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <FiImage style={{ color: '#6b7280' }} />
                <span style={{ flex: 1 }}>
                  <strong>{t('submitPayment.alreadyPaidTitle', { defaultValue: 'Already paid? Upload proof' })}</strong>
                  <span style={{ color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>
                    {t('submitPayment.alreadyPaidHint', { defaultValue: 'Bank transfer, cash, or any other channel' })}
                  </span>
                </span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>{showManualForm ? '▴' : '▾'}</span>
              </button>
            </>
            );
          })()}

          {/* Manual/screenshot flow — always visible when Lenco is not
              configured, or when the member has expanded the toggle. */}
          {(!lencoConfigured || showManualForm) && (
            <div className="form-grid" style={{ marginTop: 16 }}>
              <div className="form-group form-full">
                <label>{t('finance.paymentMethod')} <span className="required">*</span></label>
                <select value={form.payment_method} onChange={set('payment_method')}>
                  <option value="mobile">{t('finance.mobile')}</option>
                  <option value="bank">{t('finance.bank')}</option>
                </select>
              </div>

              <div className="form-group form-full">
                <label>{t('common.notes')}</label>
                <textarea value={form.notes} onChange={set('notes')} rows={2} />
              </div>

              <div className="form-group form-full">
                <label>
                  {t('submitPayment.transactionScreenshot')}
                  <span className="required"> *</span>
                  <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11, marginLeft: 6 }}>
                    {t('submitPayment.requiredHint')}
                  </span>
                </label>

                {!preview ? (
                  <div
                    onClick={() => fileRef.current?.click()}
                    style={{ border: '2px dashed #e5e7eb', borderRadius: 10, padding: '32px 24px', textAlign: 'center', cursor: 'pointer', background: '#f8fafc', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#4f46e5'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}
                  >
                    <FiUpload style={{ fontSize: 28, color: '#9ca3af', marginBottom: 8 }} />
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{t('submitPayment.clickToUpload')}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{t('submitPayment.fileSizeHint')}</div>
                  </div>
                ) : (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={preview} alt="Screenshot" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                    <button
                      className="btn-icon danger"
                      onClick={() => { setScreenshot(null); setPreview(null); }}
                      style={{ position: 'absolute', top: 8, right: 8, background: '#fff', borderRadius: '50%', padding: 4 }}
                    >
                      <FiX />
                    </button>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
              </div>

              <div className="form-group form-full">
                <label>{t('submitPayment.submittedBy')} <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>{t('submitPayment.autoHint')}</span></label>
                <input value={user?.name || ''} disabled style={{ background: '#f3f4f6', fontWeight: 600 }} />
              </div>

              <div className="form-full" style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || lencoPaying}>
                  {saving ? t('common.saving') : t('submitPayment.submitProof', { defaultValue: 'Submit proof for approval' })}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/member/dashboard')} disabled={lencoPaying}>{t('common.cancel')}</button>
          </div>
        </div>
      </div>
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
      {/* v1.8.2 — Fullscreen wait overlay while a Lenco payment is in flight.
          Sits BELOW Lenco's widget iframe (Lenco uses z-index 2147483647) so
          the widget is fully interactive on top. When the widget auto-closes
          on success or the user closes it, this overlay is what remains on
          screen while our /verify call finishes — makes it obvious to wait. */}
      {lencoPaying && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15, 23, 42, 0.72)',
          zIndex: 999998,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 20, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '28px 32px',
            maxWidth: 420, textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,.35)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              border: '5px solid #dcfce7', borderTopColor: '#16a34a',
              animation: 'lencoSpin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <div style={{ fontWeight: 700, fontSize: 17, color: '#111827', marginBottom: 6 }}>
              {t('submitPayment.lencoOverlayTitle', { defaultValue: 'Payment in progress' })}
            </div>
            <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
              {t('submitPayment.lencoOverlayBody', { defaultValue: 'Confirmation with Airtel or MTN can take up to 2 minutes. Please do NOT close this page, refresh, or press back — your receipt will appear automatically.' })}
            </div>
          </div>
          <style>{`@keyframes lencoSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
};

export default SubmitPayment;
